// @safetyhub/report-composer — 범용 검색 제안 드롭다운(suggestionsFrom 실행부, 한 벌 공유)
//
// 검색 제안을 위젯 종류(combobox)가 아니라 위젯에 붙는 직교 축으로 다룬다
// (결정 2026-08-05-composer-suggestion-source-axis). 검색 실행은 호스트 콜백
// (ComposerSuggestSearch — rc는 API를 모른다)이고, 여기는 디바운스·드롭다운·키보드만 소유한다.
// 소비처(text 위젯·TextareaListField)는 render-prop(children)으로 자기 입력 컨트롤에
// ctl(onQueryChange/onKeyDown/onBlur)을 배선한다 — 값 확정(onChange 기록)은 소비처 책임,
// writes 전파·선택 통지는 applySuggestionPick 공용 헬퍼.
//
// UI 규약은 ComboboxWidget 드롭다운 동형: body portal + fixed(조상 overflow 클리핑 탈출),
// mousedown 시점 선택(preventDefault로 input blur/닫기 race 차단), 임계 스타일 전부 인라인
// (rc 신규 Tailwind 유틸 금지 — memory rc-package-tailwind-class-purge-inline-style).
// 콤보와 달리 서버 검색형이라 후보 프리로드가 없고, 키보드 ↑↓/Enter 선택을 지원한다.
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  ComposerSuggestSelect,
  ComposerSuggestionRow,
  SuggestionsFromConfig,
} from '@safetyhub/shared';
import { applyRegisteredWrites } from './registeredWidgetWrites';
import { readBindingValue } from './RenderTemplate';

/** 검색 디바운스(ms) — 구 EditableMeasureList(대책 라이브러리 서버 검색) 동형. */
export const SUGGEST_DEBOUNCE_MS = 300;
/** 드롭다운 최대 높이(px) — 위치 측정의 위/아래 펼침 판정과 listbox maxHeight가 공유(콤보 동형). */
const SUGGEST_MENU_MAX_H = 224;

/**
 * 선택 전용 칸(requireSelection)의 빈 입력 placeholder — 아직 아무것도 안 쳤을 때도 "검색해서
 * 고르는 칸"임이 보여야 한다(은닉 금지). 소비처가 자기 placeholder가 없을 때 이걸 쓴다.
 */
export const SUGGEST_REQUIRE_SELECTION_PLACEHOLDER = '검색해 목록에서 선택';
/** 선택 전용 칸에서 검색어만 쳐 둔 상태의 안내 — 왜 값이 안 들어가는지를 그 자리에서 말한다. */
export const SUGGEST_REQUIRE_SELECTION_HINT =
  '목록에서 선택해야 입력됩니다 — 입력한 글자는 검색어로만 쓰입니다.';
/**
 * 선택 전용 칸인데 **호스트 검색이 배선되지 않은** 화면의 안내(예: 외부 위임 작성 페이지 —
 * 검색 API가 인증 뒤라 배선 자체가 불가). 그 칸의 값은 골라진 원문이어야 하는 것이므로
 * (오타·창작 문구가 결재 완료본=법적 증빙에 남는 것을 막는 보장) 자유 입력으로 열지 않는다.
 * 칸을 숨기지도 않는다(은닉 금지) — 읽기 전용으로 두고 왜 못 쓰는지를 말한다.
 */
export const SUGGEST_REQUIRE_SELECTION_UNAVAILABLE =
  '이 화면에서는 검색을 쓸 수 없어 입력할 수 없습니다 — 목록에서 선택해야 하는 칸입니다.';

/**
 * 타이핑한 글자를 값으로 커밋해도 되는가 — **선언(requireSelection)만** 본다.
 *
 * ⚠️ 이 판정을 드롭다운 접합부(SuggestionControlBindings) 유무로 대신하면 안 된다. 호스트가
 * 검색 콜백을 안 넘긴 화면에서는 접합부가 undefined라 `!sctl?.requireSelection`이 true가 되어
 * **선언이 뒤집힌다**(선택 전용 칸이 자유 입력으로 열린다). 소비처 세 경로(표 셀·단일 text·목록형)가
 * 같은 함수를 쓰게 해 그 실수가 한 곳에서만 가능하도록 모은다.
 */
export function isTypedCommitBlocked(
  config: Partial<SuggestionsFromConfig> | undefined,
): boolean {
  return config?.requireSelection === true;
}

/**
 * 선택 전용 칸 안내 한 줄 — 검색어 초안 힌트와 미배선 degrade 안내가 같은 결로 보이게 한 벌로
 * 공유한다. 임계 시각은 전부 인라인(rc 신규 Tailwind 유틸 금지 — purge 규약).
 */
export function SuggestNote({ text }: { text: string }) {
  return (
    <p role="note" style={{ marginTop: 2, fontSize: 11, lineHeight: 1.4, color: '#b45309' }}>
      {text}
    </p>
  );
}

/**
 * 검색 미배선 degrade 컨트롤 시각 — 값은 그대로 읽히되(복사 가능) 입력할 수 없음을 드러낸다.
 * disabled가 아니라 readOnly를 쓰는 이유: 값이 폼에서 빠지지 않아야 하고, 선택·복사는 되어야 한다.
 */
export const SUGGEST_UNAVAILABLE_CONTROL_STYLE: CSSProperties = {
  backgroundColor: '#f9fafb',
  color: '#6b7280',
  cursor: 'not-allowed',
};

/** 소비처 입력 컨트롤에 배선할 접합부 — SuggestionDropdown children(render-prop)이 전달. */
export interface SuggestionControlBindings {
  /** 입력 onChange에서 호출 — 디바운스 후 호스트 검색 실행(빈/공백 query는 즉시 닫기). */
  onQueryChange: (query: string) => void;
  /**
   * 입력 onKeyDown 선처리 — true면 이벤트를 소비함(↑↓ 이동/Enter 선택/Esc 닫기 + preventDefault).
   * 소비 시 소비처의 후속 핸들러(ClickToEditField의 Esc 표시 복귀 등)를 건너뛸 것 — 드롭다운이
   * 열려 있을 때의 Esc는 "제안 닫기"이지 "편집 종료"가 아니다.
   */
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => boolean;
  /** 입력 blur 시 닫기(제안 mousedown은 preventDefault로 blur를 막으므로 선택과 race 없음). */
  onBlur: () => void;
  /**
   * 선택 전용 칸(SuggestionsFromConfig.requireSelection) — true면 소비처는 **타이핑한 값을
   * 확정하지 말 것**(onChange 기록 금지). 그 글자는 검색어일 뿐이고, 값은 제안 선택(onPick)에서만
   * 커밋된다. placeholder 기본값(SUGGEST_REQUIRE_SELECTION_PLACEHOLDER)도 이 플래그로 건다.
   */
  requireSelection: boolean;
  /**
   * 선택 전용 칸의 표시값(검색어 초안) — 소비처는 `value={ctl.draftValue ?? 확정값}`으로 배선한다.
   * null이면 확정값을 그대로 표시(자유 입력 칸은 언제나 null이라 회귀 0).
   * 선택(pick)·이탈(blur) 시 null로 돌아가므로, 고르지 않고 나가면 화면도 확정값으로 되돌아온다.
   */
  draftValue: string | null;
}

/**
 * 검색 필터 원료(filterByRowField) — 현재 값 스코프에서 그 키의 작성값을 읽어 filter로.
 * 값 스코프는 호출측 values 그대로(반복 행 안이면 행 객체, 밖이면 본문 values — 렌더 스코프
 * 추상화(values/onChange 쌍)가 이미 행/본문을 가른다). 값이 비면 필터 없이 검색(undefined).
 * dotted 키 read는 본문 readBindingValue 규약 동형(flat 우선 + getByPath 폴백).
 */
export function buildSuggestFilter(
  filterByRowField: string | undefined,
  values: Record<string, unknown>,
): Record<string, string> | undefined {
  if (!filterByRowField) return undefined;
  const v = readBindingValue(values, filterByRowField);
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  if (s === '') return undefined;
  return { [filterByRowField]: s };
}

/**
 * 같은 본문 제안 접기 — 지식베이스는 같은 문장이 여러 지식 항목에 중복 등록돼 있어(대책
 * 라이브러리 실측: 한 검색어에 동일 문장 5건) 그대로 두면 짧은 목록이 사본으로 채워진다.
 * 첫 행만 남긴다(호스트가 usage 순으로 주므로 첫 행이 가장 많이 쓰인 사본).
 */
export function dedupeSuggestionRows(rows: ComposerSuggestionRow[]): ComposerSuggestionRow[] {
  const seen = new Set<string>();
  return rows.filter((r) => {
    const k = r.text.trim();
    if (k === '' || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** 기추가 항목 제외 — excludeTexts(trim 비교)와 일치하는 제안 행을 걸러낸다(목록형 소비처). */
export function excludeSuggestionRows(
  rows: ComposerSuggestionRow[],
  excludeTexts: string[] | undefined,
): ComposerSuggestionRow[] {
  if (!excludeTexts || excludeTexts.length === 0) return rows;
  const excluded = new Set(excludeTexts.map((t) => t.trim()).filter((t) => t !== ''));
  if (excluded.size === 0) return rows;
  return rows.filter((r) => !excluded.has(r.text.trim()));
}

/** 표시 상한 — 목록이 길면 읽히지 않는다. 접기·제외로 줄어든 **뒤의** 개수 기준이다. */
export const SUGGEST_DISPLAY_LIMIT = 10;

/**
 * 호스트 응답 → 표시할 제안 행. **접기·제외를 먼저, 상한은 마지막**이라는 순서가 이 함수의 요점이다.
 *
 * 상한을 앞(=호스트 fetch)에 두면 그 상한이 중복 사본과 기추가 항목까지 세어버려, 같은 문장이
 * 여러 지식 항목에 등록된 검색어에서는 접기 후 두어 건만 남고 제외까지 겹치면 목록이 통째로
 * 비어 "검색이 안 되는 칸"으로 보인다. 그래서 호스트는 **raw를 넉넉히** 가져오고(임베딩 검색의
 * raw/cap 분리 동형) 자르는 일은 여기서 한 번만 한다.
 */
export function finalizeSuggestionRows(
  rows: ComposerSuggestionRow[],
  excludeTexts: string[] | undefined,
  limit: number = SUGGEST_DISPLAY_LIMIT,
): ComposerSuggestionRow[] {
  return dedupeSuggestionRows(excludeSuggestionRows(rows, excludeTexts)).slice(0, limit);
}

/**
 * 제안 행 선택 확정 부속 처리(값 기록은 소비처가 — text 위젯=onChange(bindingKey, row.text),
 * 목록형=항목 갱신). **선택 시에만** 호출(자유 타이핑은 전파·통지 없음 — shared 규약):
 *  - writes 전파: row.raw의 source 필드를 target에 기록 — combobox 규약 승계, 적용기는
 *    applyRegisteredWrites 재사용(반복 행 스코프면 values/onChange가 행 어댑터라 target도 행 상대키).
 *  - onSuggestSelect 통지: 호스트 부수효과(usage 증가 등) — fire-and-forget.
 */
export function applySuggestionPick(
  config: SuggestionsFromConfig,
  row: ComposerSuggestionRow,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
  onSuggestSelect?: ComposerSuggestSelect,
): void {
  if (row.raw) applyRegisteredWrites(config.writes, row.raw, values, onChange);
  onSuggestSelect?.(config.catalogKey, row);
}

/** 키보드 처리 판정(순수) — 드롭다운 닫힘/항목 0이면 무소비(입력 기본 동작 유지). */
export type SuggestKeyAction =
  | { type: 'move'; highlight: number }
  | { type: 'pick'; index: number }
  | { type: 'close' }
  | { type: 'none' };

export function suggestKeyDownAction(
  key: string,
  state: { open: boolean; count: number; highlight: number },
): SuggestKeyAction {
  if (!state.open || state.count === 0) return { type: 'none' };
  if (key === 'ArrowDown') return { type: 'move', highlight: (state.highlight + 1) % state.count };
  if (key === 'ArrowUp')
    return {
      type: 'move',
      highlight: state.highlight <= 0 ? state.count - 1 : state.highlight - 1,
    };
  if (key === 'Enter')
    // 하이라이트 없는 Enter는 무소비 — 입력 기본 동작(폼 Enter/textarea 개행) 유지.
    return state.highlight >= 0 ? { type: 'pick', index: state.highlight } : { type: 'none' };
  if (key === 'Escape') return { type: 'close' };
  return { type: 'none' };
}

/** 디바운스 + stale 응답 무효화 검색 세션(순수 — 컴포넌트/테스트 공용). reject는 결과 없음 취급. */
export interface SuggestQueryRunner {
  query: (q: string) => void;
  /** 진행 중 디바운스·미도착 응답 무효화(선택 확정/닫기/unmount). */
  cancel: () => void;
}

export function createSuggestQueryRunner(
  search: (query: string) => Promise<ComposerSuggestionRow[]>,
  onResults: (rows: ComposerSuggestionRow[]) => void,
  debounceMs = SUGGEST_DEBOUNCE_MS,
): SuggestQueryRunner {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;
  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return {
    query(q: string): void {
      clearTimer();
      seq += 1;
      // 빈/공백 query = 즉시 닫기(디바운스 없이 — 지우는 즉시 드롭다운 소멸).
      if (q.trim() === '') {
        onResults([]);
        return;
      }
      const mySeq = seq;
      timer = setTimeout(() => {
        timer = null;
        search(q).then(
          (rows) => {
            if (mySeq === seq) onResults(rows);
          },
          () => {
            // reject 무시 — 제안만 생략(입력은 계속, shared 계약).
            if (mySeq === seq) onResults([]);
          },
        );
      }, debounceMs);
    },
    cancel(): void {
      clearTimer();
      seq += 1;
    },
  };
}

/**
 * 공용 제안 드롭다운(render-prop) — 입력 컨트롤을 children으로 받아 ctl을 배선한다.
 * 결과 0 또는 빈 query면 드롭다운 미표시(open=items>0). 위치는 콤보 동형 body portal + fixed.
 * excludeTexts: 목록형 소비처가 기추가 항목을 넘기면 제안에서 제외(응답 시점 최신값 기준).
 * requireSelection: 선택 전용 칸 — 검색어 초안(draft)을 여기서 들고 있고(값은 커밋 안 됨),
 * 왜 값이 안 들어가는지도 입력 아래 한 줄로 드러낸다(은닉 금지).
 */
export function SuggestionDropdown({
  search,
  excludeTexts,
  requireSelection,
  onPick,
  children,
}: {
  search: (query: string) => Promise<ComposerSuggestionRow[]>;
  excludeTexts?: string[];
  requireSelection?: boolean;
  onPick: (row: ComposerSuggestionRow) => void;
  children: (ctl: SuggestionControlBindings) => ReactNode;
}) {
  const [items, setItems] = useState<ComposerSuggestionRow[]>([]);
  const [highlight, setHighlight] = useState(-1);
  // 선택 전용 칸의 검색어 초안 — 확정값이 아니라 이 컴포넌트 로컬 상태다(값은 pick에서만 커밋).
  // null = 초안 없음(확정값을 그대로 표시). 자유 입력 칸에서는 항상 null이라 종전 경로 그대로.
  const [draft, setDraft] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  // 최신 prop 참조(runner는 mount 시 1회 생성 — 렌더마다 재생성하면 디바운스 타이머가 끊긴다).
  const searchRef = useRef(search);
  searchRef.current = search;
  const excludeRef = useRef(excludeTexts);
  excludeRef.current = excludeTexts;
  const runnerRef = useRef<SuggestQueryRunner | null>(null);
  if (runnerRef.current === null) {
    runnerRef.current = createSuggestQueryRunner(
      (q) => searchRef.current(q),
      (rows) => {
        setItems(finalizeSuggestionRows(rows, excludeRef.current));
        setHighlight(-1);
      },
    );
  }
  useEffect(() => () => runnerRef.current?.cancel(), []);

  const open = items.length > 0;

  // 위치 측정 — 콤보 동형: open 동안 스크롤(capture)·리사이즈마다 재측정, 아래 공간 부족 시 위로.
  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const measure = (): void => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < SUGGEST_MENU_MAX_H + 12 && r.top > spaceBelow;
      setMenuPos(
        openUp
          ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4 }
          : { left: r.left, width: r.width, top: r.bottom + 4 },
      );
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // 키보드 하이라이트 항목 가시 유지(스크롤 목록).
  useEffect(() => {
    if (highlight < 0) return;
    const el = listboxRef.current?.children.item(highlight) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const close = (): void => {
    runnerRef.current?.cancel();
    setItems([]);
    setHighlight(-1);
  };
  const pick = (row: ComposerSuggestionRow): void => {
    // 초안은 역할을 다했다 — 값이 확정되므로 표시도 확정값으로 돌아간다.
    setDraft(null);
    close();
    onPick(row);
  };

  const ctl: SuggestionControlBindings = {
    requireSelection: requireSelection === true,
    draftValue: requireSelection ? draft : null,
    onQueryChange: (q) => {
      // 선택 전용 칸: 친 글자는 값이 아니라 검색어다 — 초안에만 담고 확정은 소비처가 건너뛴다.
      if (requireSelection) setDraft(q);
      runnerRef.current?.query(q);
    },
    onKeyDown: (e) => {
      const action = suggestKeyDownAction(e.key, { open, count: items.length, highlight });
      if (action.type === 'none') return false;
      e.preventDefault();
      if (action.type === 'move') setHighlight(action.highlight);
      else if (action.type === 'pick') pick(items[action.index]);
      else close();
      return true;
    },
    // Esc(위 close)는 "제안 닫기"라 초안을 남기지만, 칸을 **빠져나가면**(blur) 고르지 않은
    // 검색어는 값이 아니므로 버린다 — 표시가 확정값으로 되돌아온다.
    onBlur: () => {
      setDraft(null);
      close();
    },
  };

  return (
    // w-full: side/flex 배치 안에서도 입력 폭 유지(콤보 래퍼 동형). position은 임계라 인라인.
    <div ref={wrapRef} className="w-full" style={{ position: 'relative' }}>
      {children(ctl)}
      {/* 선택 전용 칸에서 검색어만 쳐 둔 상태의 안내 — "왜 안 들어가지"를 그 자리에서 답한다
          (은닉 금지: 값을 조용히 버리지 않는다).
          ⚠️ 선택 전용 칸에서는 **항상 렌더하고 visibility만 토글**한다. 조건부로 마운트하면
          첫 글자에 나타나고 지우면 사라져서, 표 셀 안(SI 관련법규가 그렇다)에서 타이핑 중 행
          높이가 늘었다 줄었다 한다. visibility:hidden은 자리를 그대로 차지하므로 흔들림이 0이고,
          absolute 오버레이와 달리 **줄바꿈(좁은 셀에서 2줄)까지 정확히 선점**해 다음 내용을 덮지도
          조상 overflow에 잘리지도 않는다. 자유 입력 칸은 이 안내 자체가 없어 종전 그대로(회귀 0). */}
      {ctl.requireSelection && (
        <div
          style={{ visibility: draft !== null && draft.trim() !== '' ? 'visible' : 'hidden' }}
          aria-hidden={draft === null || draft.trim() === '' ? true : undefined}
        >
          <SuggestNote text={SUGGEST_REQUIRE_SELECTION_HINT} />
        </div>
      )}
      {open && menuPos && typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={listboxRef}
            role="listbox"
            // 드롭다운 임계 스타일(위치·z·상한·박스) 전부 인라인(콤보 동형 — z 90=모달 안에서도 위).
            style={{
              position: 'fixed',
              left: menuPos.left,
              width: menuPos.width,
              ...(menuPos.top !== undefined ? { top: menuPos.top } : { bottom: menuPos.bottom }),
              zIndex: 90,
              maxHeight: SUGGEST_MENU_MAX_H,
              overflowY: 'auto',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            }}
            // 컨테이너 mousedown preventDefault — 스크롤바 조작으로도 입력 blur(=닫기)가 일어나지
            // 않게 포커스를 입력에 유지(항목 버튼 선택은 자기 핸들러가 먼저 처리).
            onMouseDown={(e) => e.preventDefault()}
          >
            {items.map((r, idx) => (
              <button
                // key에 index 합성 — 같은 텍스트 제안 2건에도 React key 고유 보장(콤보 동형 방어).
                key={`${r.text}#${idx}`}
                type="button"
                role="option"
                aria-selected={idx === highlight}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                // 키보드 하이라이트 배경(gray-100 등가) — 상태 의존 임계 시각이라 인라인.
                style={idx === highlight ? { backgroundColor: '#f3f4f6' } : undefined}
                // mousedown 시점 선택 — click까지 기다리면 input blur 닫기가 먼저 닫아버림(콤보 동형).
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(r);
                }}
              >
                {r.text}
                {r.badge ? <span className="text-gray-400"> ({r.badge})</span> : null}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
